import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { capture, putToSignedUrl } from '../api';
import { RefusalPanel } from '../components/RefusalPanel';
import { isBusy, stateLabel, type Refusal, type SheetState } from '../model';

const POLL_MS = 2000;

/**
 * The phone page (F3). No sign-in, one job, one sheet at a time.
 *
 * Everything here assumes one hand, bright light and a printer in the way.
 * The camera is opened by the file input's `capture` attribute rather than by
 * getUserMedia: the native camera gives focus tap, exposure lock and the full
 * sensor resolution, and this pipeline needs every pixel it can get -- the
 * floor is 8 pixels per hole and a tight 12 MP frame only just clears it.
 *
 * The token comes from the QR link. It is kept in component state and in the
 * URL, never in localStorage: the phone may be shared, and a capture
 * credential that outlives the walk back to the workstation is one that can be
 * used by whoever picks the phone up next.
 */
export function Capture() {
  const { jobId = '' } = useParams();
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';
  const api = useRef(capture(jobId, token)).current;

  const [job, setJob] = useState<{ name: string; mesh: number } | null>(null);
  const [sheets, setSheets] = useState<
    { sheetId: string; state: SheetState; refusal?: Refusal }[]>([]);
  const [chosen, setChosen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const ctx = await api.context();
      setJob(ctx.job);
      setSheets(ctx.sheets as typeof sheets);
      setError(null);
    } catch (e) { setError((e as Error).message); }
  }, [api]);

  useEffect(() => { void load(); }, [load]);

  const active = sheets.find((s) => s.sheetId === chosen);
  useEffect(() => {
    if (!active || !isBusy(active.state)) return;
    const timer = setInterval(() => { void load(); }, POLL_MS);
    return () => clearInterval(timer);
  }, [active, load]);

  async function send(sheetId: string, file: File) {
    setUploading(true);
    setChosen(sheetId);
    try {
      const { url } = await api.photoUpload(sheetId);
      await putToSignedUrl(url, file, 'image/jpeg');
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setUploading(false); }
  }

  if (!token) {
    return (
      <div className="phone panel stop">
        <h2>Scan the QR code</h2>
        <p>This page needs the QR code shown on the workstation.</p>
      </div>
    );
  }

  if (error && !job) {
    return (
      <div className="phone panel stop">
        <h2>Cannot open this job</h2>
        <p>{error}</p>
        <p className="muted">Scan a fresh QR code from the workstation.</p>
      </div>
    );
  }

  const waiting = sheets.filter(
    (s) => s.state === 'awaiting_photo' || s.state === 'refused'
      || s.state === 'error');

  return (
    <div className="phone stack">
      <header className="panel">
        <h2>{job?.name ?? 'Loading…'}</h2>
        {job && <p className="muted">{job.mesh}-mesh</p>}
      </header>

      <section className="panel guidance">
        <h3>Before you shoot</h3>
        <ul>
          <li>Stand over the bed, close enough that the canvas and both bars just fit.</li>
          <li>Hold the phone square to the bed, not at an angle.</li>
          <li>Both registration bars fully in frame, front and back.</li>
          <li>No glare across the canvas or over a bar.</li>
          <li>Tap to focus, wait for it to settle, then shoot.</li>
        </ul>
      </section>

      {active && (
        <section className="panel">
          <h3>Sheet {active.sheetId}</h3>
          <p className={isBusy(active.state) ? 'busy-text' : 'muted'}>
            {isBusy(active.state) && <span className="spinner" aria-hidden />}
            {stateLabel(active.state)}
          </p>
          {active.state === 'needs_review' && (
            <p>Sent to the workstation for review. You can shoot the next sheet.</p>
          )}
          {active.refusal && <RefusalPanel refusal={active.refusal} />}
        </section>
      )}

      <section className="panel">
        <h3>Take a photo</h3>
        {waiting.length === 0 && (
          <p className="muted">
            No sheets are waiting for a photo. Add one at the workstation.
          </p>
        )}
        {waiting.map((sheet) => (
          <label key={sheet.sheetId} className="capture-button">
            <input type="file" accept="image/*" capture="environment"
                   disabled={uploading}
                   onChange={(e) => {
                     const file = e.target.files?.[0];
                     if (file) void send(sheet.sheetId, file);
                   }} />
            <span>
              {uploading && chosen === sheet.sheetId
                ? 'Uploading…'
                : `Shoot sheet ${sheet.sheetId}`}
            </span>
          </label>
        ))}
      </section>

      {error && <p className="error">{error}</p>}
    </div>
  );
}
